import { m } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import useLocales from 'src/locales/use-locales';
import CryptoJS from 'crypto-js';
import { PARAMS_GRAVATAR } from 'src/config-global';
import { VERSION } from 'src/config-global';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Settings',
    linkTo: paths.dashboard.user.account.origin,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const { t } = useLocales();
  const { logout, user, refresh } = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/auth/login');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  const keyAccessAdmin = localStorage.getItem('adminAccessToken');

  const handleRevertAdminAccessKey = async () => {
    try {
      if (keyAccessAdmin) {
        localStorage.setItem('accessToken', keyAccessAdmin);
        localStorage.removeItem('adminAccessToken');
        await refresh();
        router.push(paths.dashboard.users.root);
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={`https://gravatar.com/avatar/${CryptoJS.SHA256(
            user?.email.trim().toLowerCase()
          ).toString(CryptoJS.enc.Hex)}?d=${PARAMS_GRAVATAR}`}
          alt={user?.displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {`${user?.first_name} ${user?.last_name}`}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Stack>

        {keyAccessAdmin ? (
          <Stack>
            <Divider sx={{ borderStyle: 'dashed' }} />

            <MenuItem
              onClick={handleRevertAdminAccessKey}
              sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'success.main' }}
            >
              {t('Back to Admin')}
            </MenuItem>
          </Stack>
        ) : null}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          {t('Logout')}
        </MenuItem>

        {VERSION ? <Divider sx={{ borderStyle: 'dashed' }} /> : null}

        <Box
          sx={{
            m: 1,
            px: 1,
            fontSize: '0.875rem',
            fontWeight: '400',
            fontFamily: 'Public Sans,sans-serif',
          }}
        >
          {VERSION ? 'v' + VERSION : ''}
        </Box>
      </CustomPopover>
    </>
  );
}
